import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { Buildings, ArrowRight, Check, Star, Sparkle } from '@phosphor-icons/react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const HERO_BG = "https://images.unsplash.com/photo-1757439402190-99b73ac8e807?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDV8MHwxfHNlYXJjaHwyfHxsdXh1cnklMjBtaW5pbWFsJTIwYXBhcnRtZW50JTIwaW50ZXJpb3J8ZW58MHx8fHwxNzc2MzkwMzk2fDA&ixlib=rb-4.1.0&q=85";

const NAV_ITEMS = ['How It Works', 'Compare', 'Pricing'];

const HOW_IT_WORKS = [
  { step: '01', title: 'Paste Your Airbnb / Vrbo Link', desc: 'Already listed on Airbnb, Vrbo or Booking.com? Paste the URL and our AI imports your title, address, bedrooms, description and nightly rate in 10 seconds. Review and save. Done.' },
  { step: '02', title: 'Guests Handle Themselves', desc: 'Share one link. Your guests get instant answers — check-in, WiFi, weather, restaurant recs, parking. 24/7. Without calling you.' },
  { step: '03', title: 'Problems Get Solved Without You', desc: 'Guest reports a broken AC at midnight? It gets triaged, a ticket is created, and your contractor is contacted. Before you wake up.' },
  { step: '04', title: 'You See Everything, Do Almost Nothing', desc: 'Your dashboard shows what happened overnight: messages handled, cleanings verified with photos, issues dispatched. You approve and move on.' },
];

const COMPARISON = [
  { feature: 'Annual Cost (per property)', pm: '$5,000 – $15,000', hp: 'From $948/yr' },
  { feature: 'Guest Communication', pm: 'Manual, often delayed', hp: 'AI handles chat, SMS & phone calls, 24/7' },
  { feature: 'Cleaning Coordination', pm: 'Phone calls and texts', hp: 'Auto-scheduled, photo-verified' },
  { feature: 'Maintenance Issues', pm: 'You chase vendors yourself', hp: 'Triaged, dispatched, and tracked automatically' },
  { feature: 'Guest Welcome Materials', pm: 'Generic PDF or nothing', hp: 'Personalised guide with weather, packing, local spots' },
  { feature: 'Visibility Into Your Property', pm: 'Monthly email (if you\'re lucky)', hp: 'Real-time dashboard with daily briefing' },
  { feature: 'Pricing Optimisation', pm: 'Whatever they feel like', hp: 'Data-driven nightly rate suggestions' },
  { feature: 'Review Responses', pm: 'You write them at 11pm', hp: 'AI-drafted + one-click publish to Google' },
  { feature: 'Google Review Growth', pm: 'None — you ask guests manually', hp: 'Auto review-request SMS with one-tap link' },
  { feature: 'After-Hours Calls', pm: 'Goes to voicemail', hp: 'Natural AI voice answers every call in 2 rings' },
  { feature: 'Scaling to More Properties', pm: 'More properties = more fees', hp: 'Same flat rate per property' },
];

const PLANS = [
  { name: 'Starter', price: 99, annual: 79, period: '/property/mo',
    identity: 'Run your properties yourself, without the chaos.',
    features: [
    'Handles 90%+ of guest messages automatically (web chat)',
    'Shared AI concierge SMS number — guests text one number, 24/7',
    'Personalised Welcome Guide sent to every guest',
    'Local area guide built from your address — no research needed',
    'Guests get live weather, directions, and recommendations instantly',
    'Daily dashboard briefing — know what happened while you slept',
    'Email support',
  ], cta: 'Start Saving Today', popular: false },
  { name: 'Pro', price: 149, annual: 119, period: '/property/mo',
    identity: 'Automate operations and scale without hiring.',
    features: [
    'Everything in Starter',
    'AI voice agent answers every call in 2 rings (natural ElevenLabs voice)',
    'Guest problems get triaged and your contractor gets called — automatically',
    'Cleaning auto-scheduled and verified with room-by-room photos',
    'Every booking, cleaning, and repair in one calendar',
    'Nightly rate suggestions that actually maximise your revenue',
    'Review responses drafted in seconds — publish to Google in one click',
    'Auto-SMS review request after checkout with one-tap Google review link',
    'Contractors matched to the right issue automatically',
    'Frustrated guests flagged before they leave a bad review',
    'Airbnb, Vrbo & Booking.com calendar sync automatically',
    'Priority support',
  ], cta: 'Start Saving Today', popular: true },
  { name: 'Scale', price: 199, annual: 159, period: '/property/mo',
    identity: 'Operate like a professional management company.',
    features: [
    'Everything in Pro',
    'Dedicated SMS & phone number per property (branded caller ID)',
    'Before/after photo proof on every repair — for you and your insurance',
    'Automated guest journey from pre-arrival to post-stay review request',
    'Proactive pre-arrival AI call 2h before check-in',
    'Scheduled property inspections with tracking',
    'Custom integrations',
    'Dedicated account manager',
    'SLA guarantee',
  ], cta: 'Contact Sales', popular: false },
];

const TESTIMONIALS = [
  { name: 'Sarah Mitchell', role: 'Superhost, 12 Properties', text: 'I was paying my PM $4,200/month. HostCockpit does a better job for $1,400. My phone doesn\'t ring anymore — the concierge handles everything.', saved: '$33,600/yr saved', avatar: 'https://images.unsplash.com/photo-1652471949169-9c587e8898cd?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzl8MHwxfHNlYXJjaHwzfHxwcm9mZXNzaW9uYWwlMjBidXNpbmVzcyUyMGhlYWRzaG90fGVufDB8fHx8MTc3NjMyMTA5OHww&ixlib=rb-4.1.0&q=85' },
  { name: 'James Chen', role: 'Superhost, 28 Properties', text: 'A guest reported a leak at midnight. By morning there was a ticket, my plumber had been contacted, and the guest had a status update. I didn\'t lift a finger.', saved: '$67,200/yr saved', avatar: 'https://images.unsplash.com/photo-1652471943570-f3590a4e52ed?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzl8MHwxfHNlYXJjaHwyfHxwcm9mZXNzaW9uYWwlMjBidXNpbmVzcyUyMGhlYWRzaG90fGVufDB8fHx8MTc3NjMyMTA5OHww&ixlib=rb-4.1.0&q=85' },
  { name: 'Maria Lopez', role: 'Host, 5 Properties', text: 'Five-star reviews went up 40% in one month. The personalised Welcome Guide with weather and packing tips makes me look like a professional PM. Without being one.', saved: '$12,000/yr saved', avatar: 'https://images.unsplash.com/photo-1689600944138-da3b150d9cb8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzl8MHwxfHNlYXJjaHwxfHxwcm9mZXNzaW9uYWwlMjBidXNpbmVzcyUyMGhlYWRzaG90fGVufDB8fHx8MTc3NjMyMTA5OHww&ixlib=rb-4.1.0&q=85' },
];

export default function LandingPage() {
  const navigate = useNavigate();
  const [mobileMenu, setMobileMenu] = useState(false);
  const [annual, setAnnual] = useState(true);
  const [featuredPosts, setFeaturedPosts] = useState([]);

  useEffect(() => {
    axios.get(`${API}/blog`).then(r => setFeaturedPosts((r.data || []).slice(0, 3))).catch(() => {});
  }, []);

  const scrollTo = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
    setMobileMenu(false);
  };

  return (
    <div className="min-h-screen bg-white" data-testid="landing-page">
      {/* Nav */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/70 backdrop-blur-xl border-b border-black/5" data-testid="landing-nav">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2 cursor-pointer" onClick={() => scrollTo('hero')}>
            <div className="w-8 h-8 bg-[#0052FF] rounded-sm flex items-center justify-center">
              <Buildings weight="bold" className="text-white" size={18} />
            </div>
            <span className="text-lg font-black tracking-tight" style={{ fontFamily: 'var(--font-heading)' }}>HostCockpit</span>
          </div>
          <div className="hidden md:flex items-center gap-8">
            {NAV_ITEMS.map(item => (
              <button key={item} onClick={() => scrollTo(item.toLowerCase())} className="text-sm font-medium text-zinc-600 hover:text-zinc-900 transition-colors" data-testid={`nav-${item.toLowerCase().replace(/\s/g, '-')}`}>{item}</button>
            ))}
            <Link to="/blog" className="text-sm font-medium text-zinc-600 hover:text-zinc-900 transition-colors" data-testid="nav-blog">Blog</Link>
            <Link to="/login" className="text-sm font-medium text-zinc-900 hover:text-[#0052FF] transition-colors" data-testid="nav-login">Log in</Link>
            <Link to="/register" className="px-4 py-2 bg-[#0052FF] text-white text-sm font-bold rounded-sm hover:bg-[#003EDD] transition-colors" data-testid="nav-signup">Start Saving Today</Link>
          </div>
          <button className="md:hidden p-2" onClick={() => setMobileMenu(!mobileMenu)} data-testid="mobile-menu-toggle">
            <div className="w-5 h-0.5 bg-zinc-900 mb-1"></div>
            <div className="w-5 h-0.5 bg-zinc-900 mb-1"></div>
            <div className="w-5 h-0.5 bg-zinc-900"></div>
          </button>
        </div>
        {mobileMenu && (
          <div className="md:hidden bg-white border-b border-zinc-200 px-6 py-4 space-y-3">
            {NAV_ITEMS.map(item => (<button key={item} onClick={() => scrollTo(item.toLowerCase())} className="block text-sm font-medium text-zinc-700">{item}</button>))}
            <Link to="/blog" className="block text-sm font-medium text-zinc-700" data-testid="mobile-nav-blog">Blog</Link>
            <Link to="/login" className="block text-sm font-medium text-zinc-900">Log in</Link>
            <Link to="/register" className="block px-4 py-2 bg-[#0052FF] text-white text-sm font-bold rounded-sm text-center">Start Saving Today</Link>
          </div>
        )}
      </nav>

      {/* ═══ HERO ═══ */}
      <section id="hero" className="relative pt-16 min-h-[90vh] flex items-center" data-testid="hero-section">
        <div className="absolute inset-0">
          <img src={HERO_BG} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-white via-white/95 to-white/40" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 py-32 grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <div>
            <h1 className="text-5xl sm:text-[3.5rem] tracking-tight leading-[1.05] font-black text-zinc-950 mb-6" style={{ fontFamily: 'var(--font-heading)' }}>
              Run Your Rentals<br />Without a Property<br />Manager.{' '}
              <span className="text-[#0052FF]">Save $5,000–$10,000</span> Per Property. Every Year.
            </h1>
            <p className="text-lg text-zinc-600 leading-relaxed mb-3 max-w-lg" style={{ fontFamily: 'var(--font-body)' }}>
              Save $5,000–$10,000 per property every year by automating guest communication, cleaning coordination, and daily operations. No more paying 20–30% in management fees.
            </p>
            <p className="text-xl font-black text-zinc-950 mb-3 max-w-lg" style={{ fontFamily: 'var(--font-heading)' }}>
              Same job. $5,000–$15,000 cheaper per property.
            </p>
            <p className="text-base text-zinc-600 mb-10 max-w-lg" style={{ fontFamily: 'var(--font-body)' }}>
              You stay in control. The system handles the work.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <button onClick={() => navigate('/register')} className="px-8 py-4 bg-[#0052FF] text-white font-bold text-sm rounded-sm hover:bg-[#003EDD] transition-colors flex items-center justify-center gap-2" data-testid="hero-cta">
                Start Saving Today <ArrowRight weight="bold" size={16} />
              </button>
              <button onClick={() => scrollTo('compare')} className="px-8 py-4 bg-white text-zinc-900 font-bold text-sm rounded-sm border border-zinc-900 hover:bg-zinc-50 transition-colors" data-testid="hero-secondary-cta">
                See the Comparison
              </button>
            </div>
            <div className="mt-8 flex items-center gap-6 text-sm text-zinc-500">
              <span className="flex items-center gap-1"><Check weight="bold" className="text-emerald-500" size={14} /> 14-day free trial</span>
              <span className="flex items-center gap-1"><Check weight="bold" className="text-emerald-500" size={14} /> No credit card</span>
              <span className="flex items-center gap-1"><Check weight="bold" className="text-emerald-500" size={14} /> Cancel anytime</span>
            </div>
          </div>
          <div className="hidden lg:block" />
        </div>
      </section>

      {/* ═══ SOCIAL PROOF BAR ═══ */}
      <section className="bg-zinc-950 py-6" data-testid="social-proof">
        <div className="max-w-5xl mx-auto px-6 flex flex-col md:flex-row items-center justify-center gap-8 md:gap-16 text-center">
          <div>
            <p className="text-2xl font-black text-white" style={{ fontFamily: 'var(--font-heading)' }}>340+</p>
            <p className="text-xs text-zinc-500 uppercase tracking-wide font-medium">Properties Managed</p>
          </div>
          <div className="w-px h-8 bg-zinc-800 hidden md:block" />
          <div>
            <p className="text-2xl font-black text-white" style={{ fontFamily: 'var(--font-heading)' }}>92%</p>
            <p className="text-xs text-zinc-500 uppercase tracking-wide font-medium">Guest Messages Handled by AI</p>
          </div>
          <div className="w-px h-8 bg-zinc-800 hidden md:block" />
          <div>
            <p className="text-2xl font-black text-white" style={{ fontFamily: 'var(--font-heading)' }}>$2.1M+</p>
            <p className="text-xs text-zinc-500 uppercase tracking-wide font-medium">Saved in Management Fees</p>
          </div>
          <div className="w-px h-8 bg-zinc-800 hidden md:block" />
          <div>
            <p className="text-2xl font-black text-white" style={{ fontFamily: 'var(--font-heading)' }}>4.9/5</p>
            <p className="text-xs text-zinc-500 uppercase tracking-wide font-medium">Average Host Rating</p>
          </div>
        </div>
      </section>

      {/* ═══ VALUE STRIP — AGGRESSIVE ═══ */}
      <section className="bg-zinc-50 py-20" data-testid="value-strip">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="text-2xl sm:text-3xl tracking-tight font-black text-zinc-950 mb-10 text-center" style={{ fontFamily: 'var(--font-heading)' }}>Stop Giving Away $5,000–$15,000<br />Per Property. Every Year.</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 items-center">
            <div className="text-center md:text-right">
              <p className="text-xs text-zinc-500 font-bold uppercase tracking-wide mb-2">Property Manager</p>
              <p className="text-3xl font-black text-red-500 line-through" style={{ fontFamily: 'var(--font-heading)' }}>$5,000–$15,000</p>
              <p className="text-sm text-red-400 mt-1 font-medium">20–30% of your revenue. Gone.</p>
            </div>
            <div className="text-center">
              <div className="w-14 h-14 bg-[#0052FF] rounded-full flex items-center justify-center mx-auto mb-2">
                <ArrowRight weight="bold" size={22} className="text-white" />
              </div>
              <p className="text-sm font-black text-zinc-950">You keep the difference.</p>
            </div>
            <div className="text-center md:text-left">
              <p className="text-xs text-zinc-500 font-bold uppercase tracking-wide mb-2">HostCockpit</p>
              <p className="text-3xl font-black text-emerald-600" style={{ fontFamily: 'var(--font-heading)' }}>From $948/yr</p>
              <p className="text-sm text-emerald-600 mt-1 font-medium">Flat fee. Same job done.</p>
            </div>
          </div>
          {/* Before/After testimonial */}
          <div className="mt-10 max-w-2xl mx-auto bg-white border border-zinc-200 p-6 text-center">
            <p className="text-sm text-zinc-700 italic leading-relaxed mb-3">"We were paying ~$14,000/year in management fees across 3 properties. Switched to HostCockpit and now pay under $3,600. Saved about $10,000 in the first year — and our guest ratings actually went up."</p>
            <p className="text-xs font-bold text-zinc-900">— Mark & Lisa Davis, 3 Properties, Gatlinburg TN</p>
          </div>
        </div>
      </section>

      {/* ═══ THE PAIN ═══ */}
      <section className="py-24 bg-white" data-testid="pain-section">
        <div className="max-w-3xl mx-auto px-6">
          <h2 className="text-3xl sm:text-4xl tracking-tight font-black text-zinc-950 mb-8 text-center" style={{ fontFamily: 'var(--font-heading)' }}>Managing Rental Properties Turns Into<br />a Full-Time Job. Fast.</h2>
          <div className="space-y-4 max-w-xl mx-auto mb-10">
            {[
              'Guests message you at all hours — and expect instant replies',
              'Cleaners need constant coordination between every booking',
              'Maintenance issues pop up and slip through the cracks',
              'Scaling means hiring more people or burning yourself out',
            ].map((pain, i) => (
              <div key={i} className="flex items-start gap-3" data-testid={`pain-${i}`}>
                <div className="w-5 h-5 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-red-500 text-xs font-bold">!</span>
                </div>
                <p className="text-base text-zinc-700">{pain}</p>
              </div>
            ))}
          </div>
          <div className="text-center">
            <p className="text-lg text-zinc-900 mb-2">So most hosts either:</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
              <div className="px-6 py-4 bg-red-50 border border-red-200">
                <p className="text-sm font-bold text-red-700">Burn out managing everything themselves</p>
              </div>
              <div className="px-6 py-4 bg-red-50 border border-red-200">
                <p className="text-sm font-bold text-red-700">Give up 20–30% to a property manager</p>
              </div>
            </div>
            <p className="text-lg font-bold text-[#0052FF]">There's a third option.</p>
            <p className="text-sm text-zinc-500 mt-3 italic">Most hosts don't realise how much time they're actually spending until they try to stop.</p>
          </div>
        </div>
      </section>

      {/* ═══ INSTANT SETUP — URL IMPORTER SHOWCASE ═══ */}
      <section className="py-24 bg-white border-t border-zinc-200" data-testid="instant-setup-section">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid lg:grid-cols-5 gap-12 items-center">
            <div className="lg:col-span-2">
              <div className="inline-flex items-center gap-2 px-3 py-1 bg-blue-50 border border-blue-200 rounded-sm mb-5">
                <Sparkle weight="fill" size={12} className="text-[#0052FF]" />
                <span className="text-[11px] font-black uppercase tracking-[0.15em] text-[#0052FF]">Instant Setup</span>
              </div>
              <h2 className="text-3xl sm:text-4xl tracking-tight font-black text-zinc-950 leading-[1.05] mb-5" style={{ fontFamily: 'var(--font-heading)' }}>
                Already on Airbnb or Vrbo?<br />Import your listing in <span className="text-[#0052FF]">10 seconds.</span>
              </h2>
              <p className="text-zinc-600 text-lg leading-relaxed mb-6">
                No re-typing your title, address, bedrooms or description. Our AI reads your public listing on Airbnb, Vrbo or Booking.com and pre-fills every field. Review, save, and you're live.
              </p>
              <ul className="space-y-2.5 mb-8">
                {[
                  'Works with Airbnb, Vrbo and Booking.com URLs',
                  'Auto-extracts name, address, bedrooms, bathrooms, rate, description',
                  'Review &amp; edit before saving — you stay in control',
                  'Local guide and welcome pack auto-generate from the address',
                ].map((f, i) => (
                  <li key={i} className="flex items-start gap-2.5 text-sm text-zinc-700">
                    <Check weight="bold" size={14} className="text-[#0052FF] mt-0.5 flex-shrink-0" />
                    <span dangerouslySetInnerHTML={{ __html: f }} />
                  </li>
                ))}
              </ul>
              <button onClick={() => navigate('/register')} className="inline-flex items-center gap-2 px-6 py-3 bg-[#0052FF] text-white text-sm font-bold rounded-sm hover:bg-[#003EDD] transition-colors" data-testid="instant-setup-cta">
                Try the importer free <ArrowRight weight="bold" size={14} />
              </button>
            </div>

            {/* Mock URL importer card */}
            <div className="lg:col-span-3">
              <div className="relative bg-zinc-950 p-6 sm:p-8 shadow-2xl border border-zinc-800">
                <div className="flex items-center gap-2 mb-5">
                  <div className="w-2.5 h-2.5 rounded-full bg-red-500/80" />
                  <div className="w-2.5 h-2.5 rounded-full bg-amber-500/80" />
                  <div className="w-2.5 h-2.5 rounded-full bg-emerald-500/80" />
                  <span className="ml-3 text-[11px] text-zinc-500 font-mono tracking-wider">HostCockpit · Import Listing</span>
                </div>
                <div className="bg-white p-5 rounded-sm mb-4">
                  <label className="block text-[10px] font-bold text-zinc-500 uppercase tracking-[0.15em] mb-2">Listing URL</label>
                  <div className="flex items-center gap-2 px-3 py-2.5 border border-zinc-200 bg-zinc-50 rounded-sm">
                    <span className="text-zinc-400 text-xs font-mono">🔗</span>
                    <span className="text-sm text-zinc-700 font-mono truncate">airbnb.com/rooms/12345678</span>
                  </div>
                  <button className="mt-4 w-full px-4 py-2.5 bg-[#0052FF] text-white text-sm font-bold rounded-sm inline-flex items-center justify-center gap-2">
                    <Sparkle weight="fill" size={14} /> Import listing
                  </button>
                </div>
                <div className="bg-white/95 p-5 rounded-sm space-y-2.5 text-xs">
                  <div className="flex items-center gap-2 text-emerald-600 font-bold text-[11px] uppercase tracking-wider mb-2">
                    <Check weight="bold" size={12} /> AI detected the following
                  </div>
                  {[
                    ['Name', 'Oceanfront Villa · 3BR with Pool'],
                    ['Address', 'Ucluelet, BC, Canada'],
                    ['Bedrooms / Bathrooms', '3 bed · 2 bath'],
                    ['Max guests', '6 guests'],
                    ['Nightly rate', '$285 / night'],
                  ].map(([k, v]) => (
                    <div key={k} className="flex items-center justify-between gap-3 py-1 border-b border-zinc-100 last:border-b-0">
                      <span className="text-zinc-500 font-medium">{k}</span>
                      <span className="text-zinc-900 font-bold truncate">{v}</span>
                    </div>
                  ))}
                </div>
                <div className="absolute -top-3 -right-3 bg-[#0052FF] text-white text-[10px] font-black uppercase tracking-[0.15em] px-3 py-1.5 rounded-sm shadow-lg">
                  ~10 seconds
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ═══ HOW IT WORKS ═══ */}
      <section id="how it works" className="py-32 bg-white" data-testid="how-it-works-section">
        <div className="max-w-7xl mx-auto px-6">
          <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#0052FF] mb-4">How It Works</p>
          <h2 className="text-3xl sm:text-4xl tracking-tight font-bold text-zinc-950 mb-16" style={{ fontFamily: 'var(--font-heading)' }}>Set up in minutes. Runs itself from day one.</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-0 border border-zinc-200">
            {HOW_IT_WORKS.map((s, i) => (
              <div key={i} className="p-8 border-r border-zinc-200 last:border-r-0 hover:bg-zinc-50 transition-colors" data-testid={`step-${i}`}>
                <span className="text-4xl font-black text-[#0052FF] block mb-4" style={{ fontFamily: 'var(--font-heading)' }}>{s.step}</span>
                <h3 className="text-lg font-bold text-zinc-950 mb-2" style={{ fontFamily: 'var(--font-heading)' }}>{s.title}</h3>
                <p className="text-sm text-zinc-600 leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ AI TIME SAVINGS ═══ */}
      <section className="py-16 bg-zinc-950" data-testid="time-savings">
        <div className="max-w-6xl mx-auto px-6">
          <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#0052FF] mb-6 text-center">What AI Does in Seconds That Takes Hours by Hand</p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-0 border-y border-zinc-800">
            {[
              { task: 'Welcome Guide PDF', manual: '2–3 hours to write', ai: 'Generated in 10 seconds', detail: 'Weather, packing list, 50+ local spots — personalised per booking' },
              { task: 'Local Area Research', manual: '4–6 hours per property', ai: 'Generated in 15 seconds', detail: 'Restaurants, hospitals, shopping, sightseeing — all from your address' },
              { task: 'Web Chat Replies', manual: '5–15 min per message', ai: 'Instant, 24/7', detail: 'Check-in, WiFi, weather, recommendations — handled without you' },
              { task: 'Review Drafts → Google', manual: '10–20 min per review', ai: 'Drafted in 3 seconds', detail: 'AI writes the reply, you click publish — live on Google in one tap' },
              { task: 'SMS Concierge', manual: 'You reply from bed at 2am', ai: 'AI texts back in 2 seconds', detail: 'Guests text our number — AI uses your property knowledge base' },
              { task: 'Voice AI Receptionist', manual: 'Phone calls go to voicemail', ai: 'Picks up in 2 rings', detail: 'Natural human-quality voice answers every call — even at 3am' },
              { task: 'Google Review Growth', manual: 'You beg guests manually', ai: 'One-tap SMS after checkout', detail: 'Happy guests get a text with a direct-to-review link. Star count goes up.' },
              { task: 'Maintenance Triage', manual: 'You chase vendors yourself', ai: 'Ticket + contractor in 30s', detail: 'AI detects the issue in guest chat, creates the ticket, calls the right tradesperson' },
            ].map((s, i) => (
              <div key={i} className="p-6 border-r border-b border-zinc-800 last:border-r-0" data-testid={`savings-${i}`}>
                <p className="text-sm font-bold text-white mb-2">{s.task}</p>
                <p className="text-xs text-red-400 line-through mb-1">{s.manual}</p>
                <p className="text-xs text-emerald-400 font-bold mb-2">{s.ai}</p>
                <p className="text-xs text-zinc-500">{s.detail}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ WHY THIS ACTUALLY WORKS ═══ */}
      <section className="py-24 bg-zinc-50" data-testid="why-it-works">
        <div className="max-w-3xl mx-auto px-6">
          <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#0052FF] mb-4 text-center">Why Hosts Are Switching</p>
          <h2 className="text-3xl sm:text-4xl tracking-tight font-black text-zinc-950 mb-6 text-center" style={{ fontFamily: 'var(--font-heading)' }}>Why This Actually Replaces<br />a Property Manager</h2>
          <p className="text-zinc-600 text-center mb-12 max-w-xl mx-auto">Most property managers are doing four things. HostCockpit automates all four.</p>
          <div className="space-y-0">
            {[
              { pm: 'Responding to guest messages', hp: 'AI handles guest communication instantly — 24/7, in any language, with live weather and local knowledge' },
              { pm: 'Coordinating cleaning between bookings', hp: 'Cleaning is auto-scheduled on checkout, assigned to your team, and verified with room-by-room photos' },
              { pm: 'Handling maintenance issues', hp: 'Issues are triaged automatically — AI troubleshoots first, then creates a ticket and contacts your contractor' },
              { pm: 'Keeping everything organised', hp: 'One dashboard, one calendar, daily AI briefing. Everything runs from one system.' },
            ].map((row, i) => (
              <div key={i} className="grid grid-cols-1 md:grid-cols-2 border border-zinc-200 border-t-0 first:border-t bg-white" data-testid={`mechanism-${i}`}>
                <div className="px-6 py-5 border-r border-zinc-200">
                  <p className="text-xs font-bold text-zinc-400 uppercase tracking-wide mb-1">What your PM does</p>
                  <p className="text-sm text-zinc-700">{row.pm}</p>
                </div>
                <div className="px-6 py-5 bg-blue-50/50">
                  <p className="text-xs font-bold text-[#0052FF] uppercase tracking-wide mb-1">What HostCockpit does</p>
                  <p className="text-sm text-zinc-900 font-medium">{row.hp}</p>
                </div>
              </div>
            ))}
          </div>
          <p className="text-center mt-8 text-lg font-black text-zinc-950" style={{ fontFamily: 'var(--font-heading)' }}>Same job. $5,000–$15,000 cheaper per property.</p>
          <p className="text-center mt-2 text-sm text-zinc-500 italic">The difference is you keep control — and you keep the money.</p>
        </div>
      </section>

      {/* ═══ COMPARISON ═══ */}
      <section id="compare" className="py-32 bg-zinc-50" data-testid="compare-section">
        <div className="max-w-4xl mx-auto px-6">
          <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#0052FF] mb-4 text-center">Why Switch?</p>
          <h2 className="text-3xl sm:text-4xl tracking-tight font-bold text-zinc-950 mb-4 text-center" style={{ fontFamily: 'var(--font-heading)' }}>Everything Your Property Manager Does.<br />Done Better. For a Flat Fee.</h2>
          <p className="text-zinc-600 text-lg mb-12 text-center max-w-xl mx-auto">Here's what you're actually paying for — and what you could be getting instead.</p>
          <div className="bg-white border border-zinc-200 overflow-hidden">
            <div className="grid grid-cols-3 border-b border-zinc-200 bg-zinc-50">
              <div className="px-6 py-4"></div>
              <div className="px-6 py-4 text-xs font-bold text-zinc-500 uppercase tracking-wide text-center">Property Manager</div>
              <div className="px-6 py-4 text-xs font-bold text-[#0052FF] uppercase tracking-wide text-center bg-blue-50">HostCockpit</div>
            </div>
            {COMPARISON.map((row, i) => (
              <div key={i} className="grid grid-cols-3 border-b border-zinc-100 last:border-b-0" data-testid={`compare-row-${i}`}>
                <div className="px-6 py-4 text-sm font-bold text-zinc-900">{row.feature}</div>
                <div className="px-6 py-4 text-sm text-zinc-500 text-center">{row.pm}</div>
                <div className="px-6 py-4 text-sm font-bold text-zinc-900 text-center bg-blue-50/50">{row.hp}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ LIFE AFTER — THE TRANSFORMATION ═══ */}
      <section className="py-24 bg-zinc-950" data-testid="life-after-section">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#0052FF] mb-4">The Difference</p>
          <h2 className="text-3xl sm:text-4xl tracking-tight font-bold text-white mb-12" style={{ fontFamily: 'var(--font-heading)' }}>What Changes When You Stop<br />Using a Property Manager</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-0 text-left">
            {[
              { before: 'Answering guest messages at 2am', after: 'Guests get instant replies without you lifting a finger' },
              { before: 'Texting cleaners between every booking', after: 'Cleanings happen automatically — verified with photos' },
              { before: 'Chasing contractors when something breaks', after: 'Issues get triaged and dispatched while you sleep' },
              { before: 'Paying $5K–$15K/yr per property in fees', after: 'Flat monthly fee. You keep the rest.' },
              { before: 'Scaling means hiring more people', after: 'Scale to 20+ properties without adding headcount' },
              { before: 'Running your rental like a side hustle', after: 'Running your rentals like a system' },
            ].map((row, i) => (
              <div key={i} className={`p-5 ${i % 2 === 0 ? 'border-r border-zinc-800' : ''} border-b border-zinc-800`} data-testid={`transform-${i}`}>
                <p className="text-sm text-red-400 line-through mb-2">{row.before}</p>
                <p className="text-sm text-emerald-400 font-bold">{row.after}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ PRICING ═══ */}
      <section id="pricing" className="py-32 bg-white" data-testid="pricing-section">
        <div className="max-w-7xl mx-auto px-6">
          <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#0052FF] mb-4 text-center">Simple, Transparent Pricing</p>
          <h2 className="text-3xl sm:text-4xl tracking-tight font-bold text-zinc-950 mb-4 text-center" style={{ fontFamily: 'var(--font-heading)' }}>No Contracts. No Revenue Cuts. Just a Flat Fee.</h2>
          <p className="text-zinc-600 text-lg mb-2 text-center max-w-xl mx-auto">Your property manager takes 20–30% of every booking. HostCockpit charges a flat rate. You keep the rest.</p>
          <p className="text-zinc-950 text-base font-bold mb-3 text-center" style={{ fontFamily: 'var(--font-heading)' }}>Same job. $5,000–$15,000 cheaper per property.</p>
          <p className="text-sm text-center mb-8"><span className="inline-block px-3 py-1 bg-emerald-50 border border-emerald-200 rounded-sm text-emerald-800 font-bold">✓ 14-day free trial</span> <span className="text-zinc-500 ml-2">No credit card required to start</span></p>

          {/* Toggle */}
          <div className="flex items-center justify-center gap-3 mb-12">
            <span className={`text-sm font-medium ${!annual ? 'text-zinc-900' : 'text-zinc-400'}`}>Monthly</span>
            <button onClick={() => setAnnual(!annual)} className={`relative w-12 h-6 rounded-full transition-colors ${annual ? 'bg-[#0052FF]' : 'bg-zinc-300'}`} data-testid="billing-toggle">
              <div className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-transform ${annual ? 'left-7' : 'left-1'}`} />
            </button>
            <span className={`text-sm font-medium ${annual ? 'text-zinc-900' : 'text-zinc-400'}`}>Annual</span>
            {annual && <span className="text-xs font-bold text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-sm">2 months free</span>}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-0">
            {PLANS.map((plan, i) => (
              <div key={i} className={`p-8 border border-zinc-200 ${plan.popular ? 'bg-zinc-950 text-white border-zinc-950 relative' : 'bg-white'}`} data-testid={`plan-${plan.name.toLowerCase()}`}>
                {plan.popular && <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 px-3 py-1 bg-[#0052FF] text-white text-xs font-bold tracking-wide">MOST POPULAR</div>}
                <h3 className="text-xl font-bold mb-1" style={{ fontFamily: 'var(--font-heading)' }}>{plan.name}</h3>
                <p className={`text-sm mb-4 ${plan.popular ? 'text-zinc-400' : 'text-zinc-500'}`}>{plan.identity}</p>
                <div className="flex items-baseline gap-1 mb-1">
                  <span className="text-4xl font-black" style={{ fontFamily: 'var(--font-heading)' }}>${annual ? plan.annual : plan.price}</span>
                  <span className={`text-sm ${plan.popular ? 'text-zinc-400' : 'text-zinc-500'}`}>{plan.period}</span>
                </div>
                {annual && <p className={`text-xs mb-6 ${plan.popular ? 'text-zinc-500' : 'text-zinc-400'}`}>Billed annually — ${plan.annual * 12}/yr per property</p>}
                {!annual && <p className={`text-xs mb-6 ${plan.popular ? 'text-zinc-500' : 'text-zinc-400'}`}>&nbsp;</p>}
                <ul className="space-y-3 mb-8">
                  {plan.features.map((f, j) => (
                    <li key={j} className="flex items-start gap-2 text-sm">
                      <Check weight="bold" size={14} className={`mt-0.5 flex-shrink-0 ${plan.popular ? 'text-[#0052FF]' : 'text-emerald-500'}`} />
                      <span className={plan.popular ? 'text-zinc-300' : 'text-zinc-700'}>{f}</span>
                    </li>
                  ))}
                </ul>
                <button onClick={() => navigate(`/register?plan=${plan.name.toLowerCase()}&interval=${annual ? 'year' : 'month'}`)} className={`w-full py-3 text-sm font-bold rounded-sm transition-colors ${plan.popular ? 'bg-[#0052FF] text-white hover:bg-[#003EDD]' : 'bg-zinc-900 text-white hover:bg-zinc-800'}`} data-testid={`plan-cta-${plan.name.toLowerCase()}`}>
                  {plan.cta}
                </button>
              </div>
            ))}
          </div>
          <div className="text-center mt-8 space-y-1">
            <p className="text-sm font-bold text-zinc-900">Founding pricing — early users lock this rate in for life.</p>
            <p className="text-xs text-zinc-500">Cancel anytime. No long-term contracts. No revenue cuts. Ever.</p>
          </div>
        </div>
      </section>

      {/* ═══ TESTIMONIALS ═══ */}
      <section id="testimonials" className="py-32 bg-zinc-50" data-testid="testimonials-section">
        <div className="max-w-7xl mx-auto px-6">
          <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#0052FF] mb-4">From Hosts Who Made the Switch</p>
          <h2 className="text-3xl sm:text-4xl tracking-tight font-bold text-zinc-950 mb-16" style={{ fontFamily: 'var(--font-heading)' }}>They Fired Their Property Manager.</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-0 border border-zinc-200 bg-white">
            {TESTIMONIALS.map((t, i) => (
              <div key={i} className="p-8 border-r border-zinc-200 last:border-r-0" data-testid={`testimonial-${i}`}>
                <div className="flex gap-0.5 mb-3">{[...Array(5)].map((_, j) => <Star key={j} weight="fill" size={14} className="text-amber-400" />)}</div>
                <p className="text-sm text-zinc-700 leading-relaxed mb-4">"{t.text}"</p>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <img src={t.avatar} alt={t.name} className="w-10 h-10 rounded-full object-cover" />
                    <div>
                      <p className="text-sm font-bold text-zinc-950">{t.name}</p>
                      <p className="text-xs text-zinc-500">{t.role}</p>
                    </div>
                  </div>
                  <span className="text-xs font-bold text-emerald-600 bg-emerald-50 px-2 py-1 rounded-sm">{t.saved}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ FEATURED BLOG ═══ */}
      {featuredPosts.length > 0 && (
        <section className="py-24 bg-zinc-50 border-t border-zinc-200" data-testid="featured-blog-section">
          <div className="max-w-7xl mx-auto px-6">
            <div className="flex items-end justify-between mb-12 gap-4 flex-wrap">
              <div>
                <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#0052FF] mb-2">Playbook</div>
                <h2 className="text-3xl sm:text-4xl tracking-tight font-bold text-zinc-950" style={{ fontFamily: 'var(--font-heading)' }}>Hosting insights from the frontline</h2>
                <p className="text-zinc-600 mt-3 max-w-2xl">Field-tested tactics on cutting management fees, automating guest comms, and protecting your cash flow.</p>
              </div>
              <Link to="/blog" className="text-sm font-bold text-zinc-900 hover:text-[#0052FF] inline-flex items-center gap-1 transition-colors" data-testid="featured-blog-view-all">
                View all articles <ArrowRight weight="bold" size={14} />
              </Link>
            </div>
            <div className="grid md:grid-cols-3 gap-6" data-testid="featured-blog-grid">
              {featuredPosts.map((p, i) => (
                <Link
                  key={p.slug}
                  to={`/blog/${p.slug}`}
                  className="group bg-white border border-zinc-200 rounded-sm p-7 hover:border-[#0052FF] hover:shadow-[0_4px_24px_-8px_rgba(0,82,255,0.2)] transition-all flex flex-col"
                  data-testid={`featured-blog-card-${i}`}
                >
                  <div className="text-[11px] font-bold uppercase tracking-[0.15em] text-zinc-400 mb-3">
                    {new Date(p.published_at || p.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                  </div>
                  <h3 className="text-lg font-bold text-zinc-950 leading-snug mb-3 group-hover:text-[#0052FF] transition-colors" style={{ fontFamily: 'var(--font-heading)' }}>{p.title}</h3>
                  <p className="text-sm text-zinc-600 leading-relaxed line-clamp-3 flex-1">{p.excerpt}</p>
                  <div className="mt-5 text-xs font-bold text-zinc-900 group-hover:text-[#0052FF] inline-flex items-center gap-1 transition-colors">
                    Read article <ArrowRight weight="bold" size={12} />
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ═══ FINAL CTA ═══ */}
      <section className="py-32 bg-zinc-950" data-testid="cta-section">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <h2 className="text-3xl sm:text-4xl tracking-tight font-bold text-white mb-4" style={{ fontFamily: 'var(--font-heading)' }}>Stop Giving Away 20–30%<br />of Your Revenue.</h2>
          <p className="text-white text-lg font-bold mb-2">Same job. $5,000–$15,000 cheaper per property.</p>
          <p className="text-zinc-400 mb-8">You stay in control. The system handles the work.</p>
          <button onClick={() => navigate('/register')} className="px-10 py-4 bg-[#0052FF] text-white font-bold text-sm rounded-sm hover:bg-[#003EDD] transition-colors inline-flex items-center gap-2" data-testid="final-cta">
            Start Saving Today <ArrowRight weight="bold" size={16} />
          </button>
          <div className="mt-6 flex items-center justify-center gap-6 text-xs text-zinc-500">
            <span className="flex items-center gap-1"><Check weight="bold" className="text-zinc-500" size={12} /> No contracts</span>
            <span className="flex items-center gap-1"><Check weight="bold" className="text-zinc-500" size={12} /> Cancel anytime</span>
            <span className="flex items-center gap-1"><Check weight="bold" className="text-zinc-500" size={12} /> Setup in under 10 minutes</span>
            <span className="flex items-center gap-1"><Check weight="bold" className="text-zinc-500" size={12} /> No credit card required</span>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-10 border-t border-zinc-200 bg-white" data-testid="footer">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-8">
            <div className="col-span-2">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-6 h-6 bg-[#0052FF] rounded-sm flex items-center justify-center">
                  <Buildings weight="bold" className="text-white" size={12} />
                </div>
                <span className="text-sm font-bold text-zinc-900" style={{ fontFamily: 'var(--font-heading)' }}>HostCockpit</span>
              </div>
              <p className="text-xs text-zinc-600 max-w-sm leading-relaxed">
                The AI property manager for Airbnb, Vrbo, and short-term rental owners. Keep 20-30% more of your revenue — without lifting a finger.
              </p>
            </div>
            <div>
              <p className="text-xs font-bold text-zinc-900 mb-3 uppercase tracking-wide">Product</p>
              <ul className="space-y-2 text-xs text-zinc-600">
                <li><button onClick={() => scrollTo('how')} className="hover:text-[#0052FF]">How It Works</button></li>
                <li><button onClick={() => scrollTo('compare')} className="hover:text-[#0052FF]">Compare</button></li>
                <li><button onClick={() => scrollTo('pricing')} className="hover:text-[#0052FF]">Pricing</button></li>
                <li><a href="/register" className="hover:text-[#0052FF]">Start Free Trial</a></li>
              </ul>
            </div>
            <div>
              <p className="text-xs font-bold text-zinc-900 mb-3 uppercase tracking-wide">Company</p>
              <ul className="space-y-2 text-xs text-zinc-600">
                <li><a href="mailto:hosts@hostcockpit.com" className="hover:text-[#0052FF]" data-testid="footer-email">hosts@hostcockpit.com</a></li>
                <li><a href="/sms-consent" className="hover:text-[#0052FF]">SMS Terms</a></li>
                <li><a href="/login" className="hover:text-[#0052FF]">Sign In</a></li>
              </ul>
            </div>
          </div>
          <div className="pt-6 border-t border-zinc-100 flex flex-col md:flex-row items-center justify-between gap-2">
            <p className="text-xs text-zinc-500">&copy; 2026 HostCockpit. All rights reserved.</p>
            <p className="text-xs text-zinc-400">Built for hosts who want to keep what they earn.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
